import CancelIcon from "@mui/icons-material/Clear";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { sortOnCaseSensitiveTexts } from "../util/commonUtils";
import { ACQUIRED_SHIFT, LAST_UPDATED, MULTI_STATION_ADMIN_ACCESS } from "../util/constants.js";
import { THREE_YEARS_IN_PAST } from "../util/dateHelper";
import { convert_Luxon_To_DateFns } from "../util/dateTimeFormatter.js";
import StarsDatePicker from "./fundamentals/StarsDatePicker";
import TradeDialog from "./TradeDialog.jsx";

const useStyles = makeStyles((theme) => ({
  topRow: {
    marginTop: "5px",
  },
}));

const TradeFilterFormDesktop = (props) => {
  const classes = useStyles();
  const renderDateTypeFilterMenuItems = () => {
    const dateTypes = [LAST_UPDATED, ACQUIRED_SHIFT];
    return dateTypes
      .slice()
      .sort()
      .map((dateType) => {
        return (
          <MenuItem key={dateType} value={dateType}>
            {dateType}
          </MenuItem>
        );
      });
  };

  const renderTradeTypeFilterMenuItems = () => {
    return props.allTradeTypeFilters
      .slice()
      .sort()
      .map((tradeType) => {
        return (
          <MenuItem key={tradeType} value={tradeType}>
            <Checkbox color="primary" checked={props.tradeTypeFilters.indexOf(tradeType) > -1} />
            <ListItemText primary={tradeType} />
          </MenuItem>
        );
      });
  };

  const renderStatusFilterMenuItems = () => {
    return props.allStatusFilters
      .slice()
      .sort()
      .map((status) => {
        return (
          <MenuItem key={status} value={status}>
            <Checkbox color="primary" checked={props.statusFilters.indexOf(status) > -1} />
            <ListItemText primary={status} />
          </MenuItem>
        );
      });
  };

  const renderWorkgroupFilterMenuItems = () => {
    return props.allWorkgroupFilters
      .slice()
      .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
      .map((workgroup) => {
        return (
          <MenuItem key={workgroup} value={workgroup}>
            <Checkbox color="primary" checked={props.workgroupFilters.indexOf(workgroup) > -1} />
            <ListItemText primary={workgroup} />
          </MenuItem>
        );
      });
  };

  const renderStationFilterMenuItems = () => {
    return props.stations
      .slice()
      .sort()
      .map((station) => {
        return (
          <MenuItem key={station} value={station} disabled={!props.featureConfig.userStations.includes(station)}>
            {station}
          </MenuItem>
        );
      });
  };

  const SearchButton = () => {
    return (
      <IconButton
        size="small"
        className="search-icon-button"
        onMouseDown={props.onSearchSubmit.bind(this, props.searchString)}
      >
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelIconButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onSearchCancel.bind(this)}>
        <CancelIcon />
      </IconButton>
    );
  };
  return (
    <Grid container className={classes.topRow} data-testid="trade-filter-form-desktop">
      <Grid item xs={3}>
        <Typography component={"div"}>
          <Box className="page-title">{props.title}</Box>
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Grid container justifyContent="center" spacing={0}>
          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <FormControl variant="outlined">
                    <InputLabel>Date Type</InputLabel>
                    <Select
                      id="tradeFormTypeSelectElement"
                      className="filter-input"
                      value={props.dateTypeFilter}
                      onChange={props.onDateTypeFilterChange.bind(this)}
                      label="Date Type"
                    >
                      <MenuItem disabled>
                        <em>Filter on Date Type</em>
                      </MenuItem>
                      {renderDateTypeFilterMenuItems()}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {props.tradeTypeFilters.length > 0 ? (
                    <FormControl variant="outlined">
                      <InputLabel>Trade Type</InputLabel>
                      <Select
                        id="tradeFormTypeSelectElement"
                        className="filter-input"
                        label="Trade Type"
                        multiple={true}
                        value={props.tradeTypeFilters}
                        onChange={props.onTradeTypeFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allTradeTypeFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Trade Type</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isTradeTypeSelectAllChecked()}
                            indeterminate={props.isTradeTypeSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderTradeTypeFilterMenuItems()}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl variant="outlined">
                      <InputLabel>Trade Type</InputLabel>
                      <Select
                        error
                        id="tradeFormTypeSelectElement"
                        className="filter-input"
                        label="Trade Type"
                        multiple={true}
                        value={props.tradeTypeFilters}
                        onChange={props.onTradeTypeFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allTradeTypeFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Trade Type</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isTradeTypeSelectAllChecked()}
                            indeterminate={props.isTradeTypeSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderTradeTypeFilterMenuItems()}
                      </Select>
                      <FormHelperText error>At least one Trade Type must be selected</FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsDatePicker
                    label="Start Date"
                    date={props.startDateFilter}
                    onChange={props.onStartDateFilterChange.bind(this)}
                    dateErrors={props.startDateFilterErrors}
                    minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
                    className="filter-input"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {props.statusFilters.length > 0 ? (
                    <FormControl variant="outlined">
                      <InputLabel>Status</InputLabel>
                      <Select
                        id="statusFilterSelectElement"
                        className="filter-input"
                        label="Status"
                        multiple={true}
                        value={props.statusFilters}
                        MenuProps={{ autoFocus: false }}
                        onChange={props.onStatusFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allStatusFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Status</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isStatusSelectAllChecked()}
                            indeterminate={props.isStatusSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderStatusFilterMenuItems()}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl variant="outlined">
                      <InputLabel>Status</InputLabel>
                      <Select
                        error
                        id="statusFilterSelectElement"
                        className="filter-input"
                        label="Status"
                        multiple={true}
                        value={props.statusFilters}
                        MenuProps={{ autoFocus: false }}
                        onChange={props.onStatusFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allStatusFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Status</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isStatusSelectAllChecked()}
                            indeterminate={props.isStatusSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderStatusFilterMenuItems()}
                      </Select>
                      <FormHelperText error>At least one Status must be selected</FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsDatePicker
                    label="End Date"
                    date={props.endDateFilter}
                    onChange={props.onEndDateFilterChange.bind(this)}
                    dateErrors={props.endDateFilterErrors}
                    className="filter-input"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {props.workgroupFilters.length > 0 ? (
                    <FormControl variant="outlined">
                      <InputLabel>Workgroup</InputLabel>
                      <Select
                        id="workgroupFilterSelectElement"
                        className="filter-input"
                        label="Workgroup"
                        multiple={true}
                        value={props.workgroupFilters}
                        MenuProps={{ autoFocus: false }}
                        onChange={props.onWorkgroupFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allWorkgroupFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Workgroup</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isWorkgroupSelectAllChecked()}
                            indeterminate={props.isWorkgroupSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderWorkgroupFilterMenuItems()}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl variant="outlined">
                      <InputLabel>Workgroup</InputLabel>
                      <Select
                        error
                        id="workgroupFilterSelectElement"
                        className="filter-input"
                        label="Workgroup"
                        multiple={true}
                        value={props.workgroupFilters}
                        MenuProps={{ autoFocus: false }}
                        onChange={props.onWorkgroupFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allWorkgroupFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Workgroup</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isWorkgroupSelectAllChecked()}
                            indeterminate={props.isWorkgroupSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderWorkgroupFilterMenuItems()}
                      </Select>
                      <FormHelperText error>At least one Workgroup must be selected</FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Grid container>
              {props.isAdminPage ? (
                <Grid item xs={12}>
                  {props.loggedInUserData.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                    <Box mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel>Station</InputLabel>
                        <Select
                          id="stationFilterSelectElement"
                          className="filter-input"
                          label="Station"
                          value={props.stationFilter}
                          onChange={props.onStationFilterChange}
                        >
                          <MenuItem disabled>
                            <em>Filter on Station</em>
                          </MenuItem>
                          {renderStationFilterMenuItems()}
                        </Select>
                      </FormControl>
                    </Box>
                  ) : (
                    <Box mt={1} mb={1}>
                      <TextField
                        className="filter-input"
                        label="Station"
                        variant="outlined"
                        value={props.stationFilter}
                        disabled
                      />
                    </Box>
                  )}
                </Grid>
              ) : (
                ""
              )}
              <Grid item xs={12}>
                {props.searchStringErrors !== "" ? (
                  <Box mt={1} mb={1}>
                    <TextField
                      className="filter-input"
                      label="Search"
                      variant="outlined"
                      value={props.searchString}
                      onChange={props.onSearchChange.bind(this)}
                      onBlur={props.onSearchBlur.bind(this)}
                      InputProps={{
                        endAdornment:
                          props.searchString !== "" ? (
                            <InputAdornment position="end">
                              {props.tradeFoundByReferenceNumberSearchIsLoading ? <CircularProgress size={16} /> : ""}
                              <CancelIconButton />
                              <SearchButton />
                            </InputAdornment>
                          ) : (
                            <InputAdornment position="end">
                              <SearchButton />
                            </InputAdornment>
                          ),
                      }}
                      onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
                      error
                      helperText={props.searchStringErrors}
                    />
                  </Box>
                ) : (
                  <Box mt={1} mb={1}>
                    <TextField
                      className="filter-input"
                      label="Search"
                      variant="outlined"
                      value={props.searchString}
                      onChange={props.onSearchChange.bind(this)}
                      onBlur={props.onSearchBlur.bind(this)}
                      InputProps={{
                        endAdornment:
                          props.searchString !== "" ? (
                            <InputAdornment position="end">
                              {props.tradeFoundByReferenceNumberSearchIsLoading ? <CircularProgress size={16} /> : ""}
                              <CancelIconButton />
                              <SearchButton />
                            </InputAdornment>
                          ) : (
                            <InputAdornment position="end">
                              <SearchButton />
                            </InputAdornment>
                          ),
                      }}
                      onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {props.tradeFoundByReferenceNumberSearchIsLoading ? (
        ""
      ) : (
        <TradeDialog
          loggedInUserData={props.loggedInUserData}
          trade={props.tradeFoundByReferenceNumberSearch}
          isDialogOpen={props.tradeFoundByReferenceNumberSearchIsDialogOpen}
          handleClickDialogOpen={props.onClickTradeFoundByReferenceNumberSearchDialogOpen.bind(this)}
          handleClickDialogClose={props.onClickTradeFoundByReferenceNumberSearchDialogClose.bind(this)}
          isAdminPage={props.isAdminPage}
          eid={props.loggedInUserData.eid}
          isDesktopView={props.isDesktopView}
        />
      )}
    </Grid>
  );
};

TradeFilterFormDesktop.propTypes = {
  loggedInUserData: PropTypes.object.isRequired,
  isAdminPage: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
  dateTypeFilter: PropTypes.string.isRequired,
  onDateTypeFilterChange: PropTypes.func.isRequired,
  startDateFilter: PropTypes.object.isRequired,
  startDateFilterErrors: PropTypes.string.isRequired,
  onStartDateFilterChange: PropTypes.func.isRequired,
  endDateFilter: PropTypes.object.isRequired,
  endDateFilterErrors: PropTypes.string.isRequired,
  onEndDateFilterChange: PropTypes.func.isRequired,
  tradeTypeFilters: PropTypes.array.isRequired,
  allTradeTypeFilters: PropTypes.array.isRequired,
  onTradeTypeFilterChange: PropTypes.func.isRequired,
  isTradeTypeSelectAllChecked: PropTypes.func.isRequired,
  isTradeTypeSelectAllIndeterminate: PropTypes.func.isRequired,
  statusFilters: PropTypes.array.isRequired,
  allStatusFilters: PropTypes.array.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  isStatusSelectAllChecked: PropTypes.func.isRequired,
  isStatusSelectAllIndeterminate: PropTypes.func.isRequired,
  workgroupFilters: PropTypes.array.isRequired,
  allWorkgroupFilters: PropTypes.array.isRequired,
  onWorkgroupFilterChange: PropTypes.func.isRequired,
  isWorkgroupSelectAllChecked: PropTypes.func.isRequired,
  isWorkgroupSelectAllIndeterminate: PropTypes.func.isRequired,
  stationFilter: PropTypes.string.isRequired,
  searchString: PropTypes.string.isRequired,
  searchStringErrors: PropTypes.string.isRequired,
  tradeFoundByReferenceNumberSearch: PropTypes.object,
  tradeFoundByReferenceNumberSearchIsLoading: PropTypes.bool.isRequired,
  tradeFoundByReferenceNumberSearchIsDialogOpen: PropTypes.bool.isRequired,
  onClickTradeFoundByReferenceNumberSearchDialogOpen: PropTypes.func.isRequired,
  onClickTradeFoundByReferenceNumberSearchDialogClose: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchBlur: PropTypes.func.isRequired,
  onSearchEnterKeyPressed: PropTypes.func.isRequired,
  onSearchCancel: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,

  // Trade Admin only props
  stations: PropTypes.array,
  onStationFilterChange: PropTypes.func,
};

export default TradeFilterFormDesktop;
